/**
 * Shared chat layout, motion and surface values.
 *
 * These are read by the composer, the thread, the header and the attachment
 * chips, so they live beside the feature rather than inside whichever component
 * happened to need them first.
 */

/**
 * The hero composer stays a focused, centred field — it is the only thing on
 * that screen, so it does not want the full window.
 */
export const HERO_COMPOSER_MAX_WIDTH = 768;

/**
 * An open thread uses the window it has: messages, header and the docked
 * composer share this cap so their edges line up. The cap only bites on very
 * wide displays, where unbounded lines would get hard to read.
 */
export const CHAT_COLUMN_MAX_WIDTH = 1440;

/** Side gutter (theme spacing units) for the thread's content and composer. */
export const CHAT_COLUMN_GUTTER = 6;

/** How many attachment chips are listed before collapsing into "+N more". */
export const MAX_VISIBLE_CHIPS = 3;

/**
 * Hero → dock composer flight. Long enough to be followed rather than glimpsed,
 * on a single deep ease-out so the composer coasts into its dock instead of
 * stopping dead. One curve for the whole trip — intermediate keyframes would
 * break the constant deceleration that makes it read as smooth.
 */
export const COMPOSER_MORPH = {
  durationMs: 3000,
  easing: 'cubic-bezier(.16,.84,.24,1)',
} as const;

/**
 * Neutrals the chat surface is built from. They sit outside the MUI palette
 * because they describe this one screen's paper-and-hairline treatment rather
 * than app-wide semantic colour.
 */
export const CHAT_TOKENS = {
  /** Card and control borders. */
  hairline: '#EBEBEB',
  /** The docked composer's input field. */
  field: '#F2F2F4',
  /** Answer actions, sent chips, quiet pills. */
  surface: '#F5F5F5',
  /** Hover state for controls sitting on white. */
  hover: '#F7F7F8',
  /** Placeholder and de-emphasised icon text. */
  muted: '#9A9A9A',
  /** Deep Research ink + wash. */
  deepInk: '#0F4C99',
  deepWash: '#EAF2FE',
} as const;
